import { useCallback, useMemo, useState } from 'react';
import { compareEntityIds, DEFAULT_LIST_SORT } from './sortById';

export function compareSortValues(a, b, type = 'string') {
  const empty = v =>
    v === null || v === undefined || v === '' || (typeof v === 'number' && Number.isNaN(v));

  if (empty(a) && empty(b)) return 0;
  if (empty(a)) return 1;
  if (empty(b)) return -1;

  if (type === 'id') {
    return compareEntityIds(String(a), String(b));
  }
  if (type === 'number') {
    const na = Number(a);
    const nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
    return String(a).localeCompare(String(b), undefined, { numeric: true });
  }
  if (type === 'date') {
    const ta = new Date(a).getTime();
    const tb = new Date(b).getTime();
    if (Number.isFinite(ta) && Number.isFinite(tb)) return ta - tb;
    return String(a).localeCompare(String(b));
  }
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * @param {Array} rows
 * @param {{ key: string, dir: 'asc'|'desc' }} sort
 * @param {Array<{ key: string, type?: string, getValue: (row: any) => any, defaultDir?: 'asc'|'desc' }>} columns
 */
export function sortTableRows(rows, sort, columns) {
  if (!rows?.length) return rows ? [] : [];
  const col = columns.find(c => c.key === sort?.key) || columns.find(c => c.key === 'id');
  const dir = sort?.dir === 'asc' ? 1 : -1;

  return [...rows].sort((rowA, rowB) => {
    const type = col?.type || (col?.key === 'id' ? 'id' : 'string');
    const va = col?.getValue ? col.getValue(rowA) : rowA?.[col?.key];
    const vb = col?.getValue ? col.getValue(rowB) : rowB?.[col?.key];
    return compareSortValues(va, vb, type) * dir;
  });
}

export function useSortableTable(rows, columns, options = {}) {
  const defaultSort = options.defaultSort || DEFAULT_LIST_SORT;
  const [sort, setSort] = useState(defaultSort);

  const toggleSort = useCallback(
    key => {
      setSort(prev => {
        if (prev.key === key) {
          return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
        }
        const col = columns.find(c => c.key === key);
        const dir = col?.defaultDir || (col?.type === 'id' ? 'desc' : 'asc');
        return { key, dir };
      });
    },
    [columns]
  );

  const sortedRows = useMemo(
    () => sortTableRows(rows, sort, columns),
    [rows, sort, columns]
  );

  return { sort, toggleSort, sortedRows, setSort };
}
